import { useEffect, useState, useRef } from 'react';
import { RouteComponentProps, useNavigate } from '@reach/router';
import { useSelector, useDispatch } from 'react-redux';
import { useSubmitPolicyStreamQuote } from '../../../hooks';
import {
  getLastSubmittedQuote,
  getSelectedPlan,
  policystreamStore,
} from '../../../store/policystreamStore';
import { errorify } from '../../../util';
import { createApplicationFinishedThunk } from '../../../store/analyticsStore';
import { usePolicyStreamQuoteSubmission } from '../../../hooks/usePolicyStreamQuoteSubmission';
import { PolicystreamQuoteSubmissionProgress } from './PolicystreamQuoteSubmissionProgress/PolicystreamQuoteSubmissionProgress';
import { usePolicyStreamGoogleAnalytics } from '../../../hooks/usePolicyStreamGoogleAnalytics';
import {
  GAEventNames,
  EventActions,
} from '../../../hooks/usePolicyStreamGoogleAnalytics/types';
import { PolicyStreamButtonsContainer } from '../components';
import { PolicyStreamPageContainer } from '../components/PageContainer';
import { policyStreamPaths } from '../policyStreamConstants';
import LoadingButton from '@mui/lab/LoadingButton';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Divider from '@mui/material/Divider';
import { styled } from '@mui/material';
import { UnifiedCompletedForm } from '../../../Typescript/classes';
import {
  getAnsweredForms,
  getAllForms,
} from '../../../store/policystreamStore';
import { hybridAnswers } from '../../util/Quote';
import { getPolicyStreamFormLabel, sortByInternalName } from './utility';
import { QuestionAnswerDisplay } from '../../common/QuestionAnswerDisplay';
import { PolicyStreamFormEditButton } from './PolicyStreamFormEditButton';
import { Link } from '@reach/router';
import { PolicystreamPlan } from '../../../store/policystreamStore';
import { PolicyStreamPlanIcon } from '../PolicyStreamPlanSelectionPage';
import { POLICYSTREAM_FORMS } from '../constants';
import { PolicyStreamSignature } from './PolicyStreamSignature';
import { PolicyStreamLayout } from '../components';
import RedoIcon from '@mui/icons-material/Redo';
import { formatAsDollar } from '../utility';
import { usePolicyStreamPlanPaymentFrequency } from '../hooks';
import { PolicyStreamAdditionalPlanDetails } from '../PolicyStreamPlanSelectionPage/PolicyStreamDisplayPlan';
import {
  MissingInformation,
  useMissingBasicInfoGuard,
  useMissingClientInfoGuard,
  useMissingPlanSelectionGuard,
  useMissingGeneralInformationGuard,
} from '../components/MissingInformation';
import { CoreQuotePolicystreamPlanPaymentFrequencyChoices as PlanPaymentFrequencyChoices } from '../../../gql/graphql';
import { ReviewDisclaimersDisplay } from './ReviewDisclaimersDisplay';

const QUOTE_PROGRESS_DISPLAY_DELAY_MS =
  process.env.NODE_ENV === 'development' ? 0 : 5 * 1000;

interface PolicyStreamReviewPageProps extends RouteComponentProps {}
export const PolicyStreamReviewPage = (_props: PolicyStreamReviewPageProps) => {
  const myAnalytics = usePolicyStreamGoogleAnalytics();

  const dispatch = useDispatch();
  const submitCompleteQuote = useSubmitPolicyStreamQuote({
    status: 'COMPLETE',
  });
  const selectedPlan = useSelector(getSelectedPlan);
  const [loading, setLoading] = useState<boolean>(false);
  const [displayQuoteProgress, setDisplayQuoteProgress] =
    useState<boolean>(false);
  const progressTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { taskPool, quote } = usePolicyStreamQuoteSubmission({
    active: loading,
    onComplete: () => {
      dispatch(createApplicationFinishedThunk());
    },
  });

  // Send Google Analytics event on component load
  useEffect(() => {
    myAnalytics?.triggerEvent(
      GAEventNames.Review,
      EventActions.PageLoad,
      'PolicyStream Review Page',
    );
  }, [myAnalytics]);
  const [acceptsTermsAndConditions, setAcceptsTermsAndConditions] =
    useState<boolean>(false);
  const [signature, setSignature] = useState<string | null>(null);
  const lastSubmittedQuote = useSelector(getLastSubmittedQuote);
  const navigate = useNavigate();

  const missingBasicInfo = useMissingBasicInfoGuard();
  const missingClientInfo = useMissingClientInfoGuard();
  const missingGeneralInfo = useMissingGeneralInformationGuard();
  const missingSelectedPlan = useMissingPlanSelectionGuard();

  // Local state for missing string and redirect URL
  const [missingString, setMissingString] = useState<string>(
    'Basic Client Information',
  );
  const [redirectUrl, setRedirectUrl] = useState<string>('');

  useEffect(() => {
    if (missingBasicInfo) {
      setMissingString(missingBasicInfo.missing);
      setRedirectUrl(policyStreamPaths.baseInformation.url);
    } else if (missingGeneralInfo) {
      setMissingString(missingGeneralInfo.missing);
      setRedirectUrl(policyStreamPaths.planSpecificQuestions.url);
    } else if (missingSelectedPlan) {
      setMissingString(missingSelectedPlan.missing);
      setRedirectUrl(policyStreamPaths.planSelection.url);
    } else if (missingClientInfo) {
      setMissingString(missingClientInfo.missing);
      setRedirectUrl(policyStreamPaths.postPlanSelectionInformation.url);
    }
  }, [
    missingBasicInfo,
    missingGeneralInfo,
    missingSelectedPlan,
    missingClientInfo,
  ]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (progressTimeoutRef.current !== null) {
        window.clearTimeout(progressTimeoutRef.current);
      }
    };
  }, []);

  // Update errors in state when there are errors from quote submission
  useEffect(() => {
    const taskPoolData = taskPool.data?.getTaskPool;
    const error = taskPool.error || taskPoolData?.error || quote.error;

    if (error) {
      dispatch(
        policystreamStore.actions.updateErrors({
          completeQuoteSubmission: errorify(error),
        }),
      );
    }
  }, [
    taskPool.error,
    taskPool.data?.getTaskPool?.error,
    quote.error,
    dispatch,
  ]);

  // when we're done submitting the quote, navigate
  useEffect(() => {
    if (lastSubmittedQuote && lastSubmittedQuote.status === 'COMPLETE') {
      setLoading(false);
      navigate(policyStreamPaths.done.url);
    }
  }, [lastSubmittedQuote, navigate]);

  return (
    <PolicyStreamLayout>
      {displayQuoteProgress ? (
        <PolicystreamQuoteSubmissionProgress
          taskPool={taskPool.data?.getTaskPool ?? null}
        />
      ) : missingString && redirectUrl ? (
        <MissingInformation missing={missingString} redirectURL={redirectUrl} />
      ) : (
        <PolicyStreamPageContainer>
          <h2>Review Page</h2>
          <h3>Please take a moment to review the following information</h3>

          <ReviewQuestionsDisplay />
          <ReviewSelectedPlanDisplay />
          <ReviewDisclaimersDisplay
            acceptsTermsAndConditions={acceptsTermsAndConditions}
            setAcceptsTermsAndConditions={(accept: boolean) =>
              setAcceptsTermsAndConditions(accept)
            }
          />
          <PolicyStreamSignature
            onError={(error) => console.error(error)}
            handleImageData={(imageDate) => setSignature(imageDate)}
          />

          <WarningsList>
            <Collapse in={!signature}>
              <ListItem alignItems='flex-start'>
                <ListItemIcon sx={{ justifyContent: 'center' }}>
                  <ErrorOutlineIcon sx={{ color: 'error.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary='Signature'
                  secondary='Please sign before submitting your application'
                />
              </ListItem>
            </Collapse>
            <Collapse in={!acceptsTermsAndConditions}>
              <ListItem alignItems='flex-start'>
                <ListItemIcon sx={{ justifyContent: 'center' }}>
                  <ErrorOutlineIcon sx={{ color: 'error.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary='Terms & Conditions'
                  secondary='You must accept the terms and conditions before submitting your application'
                />
              </ListItem>
            </Collapse>
          </WarningsList>

          <PolicyStreamButtonsContainer>
            <LoadingButton
              variant='contained'
              loading={loading}
              disabled={
                !selectedPlan || !acceptsTermsAndConditions || !signature
              }
              onClick={() => {
                submitCompleteQuote({
                  signature: signature,
                });
                setLoading(true);

                // Set timeout to display quote progress after submission
                progressTimeoutRef.current = setTimeout(() => {
                  setDisplayQuoteProgress(true);
                }, QUOTE_PROGRESS_DISPLAY_DELAY_MS);
              }}
            >
              Submit Application
            </LoadingButton>
          </PolicyStreamButtonsContainer>
        </PolicyStreamPageContainer>
      )}
    </PolicyStreamLayout>
  );
};

const WarningsList = styled(List)({});

const ReviewQuestionsDisplay = () => {
  const finalForms = useSelector(getAllForms);
  const answeredForms = useSelector(getAnsweredForms);
  const [formsWithAllQuestionsAnswered, setFormsWithAllQuestionsAnswered] =
    useState<Array<UnifiedCompletedForm>>([]);
  const [applicationFormPresent, setApplicationFormPresent] =
    useState<boolean>(false); // is the general application form present? If so, don't show the basic client info form.

  // set the forms with 'fake' answers as soon as we can
  useEffect(() => {
    const withFakeAnswers = hybridAnswers(answeredForms, finalForms);
    const sorted = [...withFakeAnswers].sort((a, b) => {
      return sortByInternalName(a.form, b.form);
    });
    setFormsWithAllQuestionsAnswered(sorted);
  }, [finalForms, answeredForms]);

  // set whether the general application form is present as soon as we have the hybrid answers
  useEffect(() => {
    setApplicationFormPresent(
      !!answeredForms.find(
        (form) => form.form.internalName === POLICYSTREAM_FORMS.applicationForm,
      ),
    );
  }, [answeredForms]);

  if (formsWithAllQuestionsAnswered.length === 0) {
    return null;
  }
  return (
    <ReviewContentsContainer>
      {[...formsWithAllQuestionsAnswered]
        .filter((form) =>
          applicationFormPresent
            ? form.form.internalName !== POLICYSTREAM_FORMS.basicClientInfo
            : true,
        )
        .map((amendedFormWithAnswers) => {
          return (
            <>
              <TitleContainer key={amendedFormWithAnswers.form.id}>
                <Typography variant='h6' color='primary'>
                  {getPolicyStreamFormLabel(amendedFormWithAnswers)}
                </Typography>
                {amendedFormWithAnswers.form.internalName ? (
                  <PolicyStreamFormEditButton
                    internalName={amendedFormWithAnswers.form.internalName}
                  />
                ) : null}
              </TitleContainer>
              <AnswerContents key={amendedFormWithAnswers.form.id}>
                {amendedFormWithAnswers.answers.map(
                  (answer, index, answers) => {
                    return (
                      <QuestionAnswerDisplay
                        form={amendedFormWithAnswers}
                        topLevelAnswer={answer}
                        edit={true}
                        answer={answer}
                        answers={answers}
                        indent={0}
                        key={answer.apiName + index}
                      />
                    );
                  },
                )}
              </AnswerContents>
              <Divider variant='middle' sx={{ mb: 4 }} />
            </>
          );
        })}
    </ReviewContentsContainer>
  );
};

const AnswerContents = styled('dl')(({ theme }) => {
  return {
    display: 'grid',
    gridTemplateColumns: '[question] 2fr [answer] 2fr',
    '& dt': {
      display: 'inline',
      ...theme.typography.body2,
    },
    '& dd': {
      display: 'inline-flex',
      flexDirection: 'row',
      ...theme.typography.body2,
      // justifyContent: 'flex-end',
      textAlign: 'left',
      color: theme.palette.primary.main,
      marginBottom: theme.spacing(2),
    },
    '& dd:after': {
      content: '" "',
      display: 'block',
    },
  };
});
const TitleContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const ReviewSelectedPlanDisplay = () => {
  const selectedPlan = useSelector(getSelectedPlan);
  return (
    <ReviewContentsContainer>
      <Typography variant='h3'>Selected Plan</Typography>
      {selectedPlan ? (
        <ReviewPageDisplaySelectedPlan plan={selectedPlan} />
      ) : (
        <NoPlanSelected />
      )}
    </ReviewContentsContainer>
  );
};

interface ReviewPageDisplaySelectedPlanProps {
  plan?: PolicystreamPlan | null;
  planFrequency?: string;
  displayButton?: boolean;
}
export const ReviewPageDisplaySelectedPlan = ({
  plan,
  planFrequency,
  displayButton = true,
}: ReviewPageDisplaySelectedPlanProps) => {
  const navigate = useNavigate();
  const [premiumDescription, setPremiumDescription] = useState<string>('');
  const [termLengthDescription, setTermLengthDescription] =
    useState<string>('');
  const [coverageAmountDescription, setCoverageAmountDescription] =
    useState<string>('');
  const { monthly, annually } = usePolicyStreamPlanPaymentFrequency();

  // set the description of the premium, term length, and coverage amount
  useEffect(() => {
    if (!plan) {
      return;
    }
    if (
      (monthly || planFrequency === PlanPaymentFrequencyChoices.Monthly) &&
      plan.premiumMonthlyDollars
    ) {
      setPremiumDescription(`$${plan.premiumMonthlyDollars} Monthly`);
    } else if (
      (annually || planFrequency === PlanPaymentFrequencyChoices.Annually) &&
      plan.premiumAnnualDollars
    ) {
      setPremiumDescription(`$${plan.premiumAnnualDollars} Annually`);
    }
    if (plan.coverageAmountDollars) {
      const coverageAsNumber = Number(plan.coverageAmountDollars);
      if (!Number.isNaN(coverageAsNumber) && isFinite(coverageAsNumber)) {
        setCoverageAmountDescription(formatAsDollar(coverageAsNumber));
      }
    }
    if (plan.termLength) {
      setTermLengthDescription(`${plan.termLength} Years`);
    }
  }, [monthly, annually, plan]);

  if (!plan) {
    return null;
  }

  return (
    <SelectedPlanContainer>
      <InsurerNameContainer>
        <IconContainer>
          <PolicyStreamPlanIcon insurer={plan.insurer} sx={{ width: '100%' }} />
        </IconContainer>

        <PlanInformationContainer>
          <Typography variant='h4'>{plan.insurer?.displayName}</Typography>
          <Typography>
            {[
              premiumDescription,
              termLengthDescription,
              coverageAmountDescription,
            ]
              .filter((elem) => !!elem)
              .join(' / ')}
          </Typography>
          <AdditionalPlanInformationContainer>
            {plan.markdownDetails ? (
              <PolicyStreamAdditionalPlanDetails plan={plan} />
            ) : null}
          </AdditionalPlanInformationContainer>
        </PlanInformationContainer>
      </InsurerNameContainer>
      {displayButton && (
        <Button
          size='small'
          onClick={() => navigate(policyStreamPaths.planSelection.url)}
          endIcon={<RedoIcon />}
        >
          Switch Plans
        </Button>
      )}
    </SelectedPlanContainer>
  );
};

const IconContainer = styled('div')(({ theme }) => {
  return {
    maxWidth: '33.3%',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
    },
  };
});
const PlanInformationContainer = styled('div')({});

const AdditionalPlanInformationContainer = styled('div')({});
const SelectedPlanContainer = styled('div')({});
const InsurerNameContainer = styled('div')(({ theme }) => {
  return {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  };
});

const NoPlanSelected = () => {
  return (
    <Typography>
      No plan has been selected! Please{' '}
      <Link to={policyStreamPaths.planSelection.url}>
        select one before continuing.
      </Link>
    </Typography>
  );
};

const ReviewContentsContainer = styled('div')(({ theme }) => {
  return {
    padding: theme.spacing(1),
  };
});
